import { Capacitor, type PluginListenerHandle } from '@capacitor/core';
import { App } from '@capacitor/app';
import { Geolocation, PermissionStatus, Position } from '@capacitor/geolocation';
import { PermissionState } from '@capacitor/core/types/definitions';

import { isAndroidPlatform } from '@ocx/utils-device';
import { enqueueNativePermissionRequest, wait } from '@ocx/utils';

import { Coordinates } from '../../modules/stores/types';
import { calculateDistance } from './utils/calculateDistance';

const DEFAULT_GEO_TIMEOUT_MS = 5000; // 5 seconds
const MAX_POSITION_CACHED_TIME_MS = 30000; // 30 seconds
const SIGNIFICANT_LOCATION_CHANGE_DISTANCE_M = 15;
const ANDROID_PERMISSION_STATUS_UPDATE_TIMEOUT = 300;
const PERMISSION_DENIED_ERROR_CODE = 1;
export const GEOLOCATION_CHANGED_EVENT = 'change';

const isWeb = !Capacitor.isNativePlatform();

const isSignificantChange = (a: Coordinates, b: Coordinates) => {
  const distance = calculateDistance(a, b, 'km') * 1000;
  return distance >= SIGNIFICANT_LOCATION_CHANGE_DISTANCE_M;
};

const positionToCoordinates = (position: Position): Coordinates => {
  return { lat: position.coords.latitude, lng: position.coords.longitude };
};

const checkPermissions = async (): Promise<PermissionStatus> => {
  try {
    return await Geolocation.checkPermissions();
  } catch (e) {
    return {
      location: 'denied',
      coarseLocation: 'denied',
    };
  }
};

const requestPermissions = async (): Promise<PermissionStatus> => {
  if (isWeb) {
    // The requestPermissions() is not possible for the Web
    // The dialog will be shown by getCurrentPosition() call
    // We decided to handle 'permission denied' for Web later.
    return { location: 'granted', coarseLocation: 'granted' };
  }
  try {
    await enqueueNativePermissionRequest(async () => {
      await Geolocation.requestPermissions();
      if (isAndroidPlatform()) {
        // Give Android time to update the system status, Samsung devices can take a bit longer OCST-17154
        await wait(ANDROID_PERMISSION_STATUS_UPDATE_TIMEOUT);
      }
    });
    return checkPermissions(); // always check permissions after requesting them
  } catch {
    return {
      location: 'denied',
      coarseLocation: 'denied',
    };
  }
};

const pickPermission = (location: PermissionState, coarseLocation: PermissionState): PermissionState => {
  if (location === 'granted' || coarseLocation === 'granted') {
    return 'granted';
  }
  if (location) {
    return location;
  }
  return coarseLocation;
};

export type GeolocationStoreState = {
  coordinates: Coordinates | null;
  status: PermissionState | null;
  loading: boolean;
  polling: boolean;
};

class GeolocationStore extends EventTarget {
  private coordinates: Coordinates | null = null;
  private status: PermissionState | null = null;
  private isPolling = false;
  private isLoading = false;
  private pollingInterval = DEFAULT_GEO_TIMEOUT_MS;
  private pollingTimeoutId: ReturnType<typeof setTimeout> | undefined = undefined;
  private pollingRequests = 0;
  private appStateListener: PluginListenerHandle | undefined = undefined;
  private activeRefreshPromise: Promise<typeof this.state> | null = null;
  private activeRefreshPromiseCanceled = false;

  private notify() {
    this.dispatchEvent(new Event(GEOLOCATION_CHANGED_EVENT));
  }

  private checkWhenAppStateChanged() {
    if (this.appStateListener) {
      return;
    }
    App.addListener('appStateChange', async (state) => {
      if (state.isActive) {
        const permissions = await checkPermissions();
        this.status = pickPermission(permissions.location, permissions.coarseLocation);

        if (this.status === 'granted') {
          await this.refresh();
        } else {
          this.coordinates = null;
          this.notify();
        }
      }
    }).then((listener) => {
      this.appStateListener = listener;
    });
  }

  private poll() {
    if (!this.isPolling) {
      return;
    }
    clearTimeout(this.pollingTimeoutId);
    this.refresh().then(() => {
      if (this.isPolling && !this.activeRefreshPromiseCanceled) {
        this.pollingTimeoutId = setTimeout(() => this.poll(), this.pollingInterval);
      }
    });
  }

  public get state(): GeolocationStoreState {
    return {
      coordinates: this.coordinates,
      status: this.status,
      loading: this.isLoading,
      polling: this.isPolling,
    };
  }

  public startPolling = (interval = DEFAULT_GEO_TIMEOUT_MS) => {
    clearTimeout(this.pollingTimeoutId);
    this.pollingRequests += 1;
    this.pollingInterval = interval;
    this.isPolling = true;
    this.poll();
  };

  public endPolling = () => {
    this.pollingRequests = Math.max(0, this.pollingRequests - 1);
    if (this.pollingRequests === 0) {
      clearTimeout(this.pollingTimeoutId);
      this.isPolling = false;
    }
  };

  public refresh = async () => {
    if (this.activeRefreshPromise) {
      return this.activeRefreshPromise;
    }

    this.activeRefreshPromiseCanceled = false;
    this.activeRefreshPromise = (async () => {
      this.isLoading = true;
      this.notify();

      try {
        const permissions = await requestPermissions();
        this.status = pickPermission(permissions.location, permissions.coarseLocation);

        // Avoid immediately prompting again through getCurrentPosition() when the
        // Android request was dismissed, denied, or not answered. Other platforms
        // keep the previous behaviour, where getCurrentPosition() may prompt.
        const skipPositionFetch = isAndroidPlatform() ? this.status !== 'granted' : this.status === 'denied';

        if (this.activeRefreshPromiseCanceled || skipPositionFetch) {
          return this.state;
        }

        let position;
        const options = {
          // Polling: Timeout should be >= 5 sec
          // Regular: Timeout should be longer - 4x (first or cold A-GPS start) >= 20 sec
          timeout: this.isPolling ? Math.max(DEFAULT_GEO_TIMEOUT_MS, this.pollingInterval) : DEFAULT_GEO_TIMEOUT_MS * 4,
          maximumAge: MAX_POSITION_CACHED_TIME_MS,
        };

        if (isAndroidPlatform()) {
          // Some Samsung/Android devices fail on the first getCurrentPosition()
          // right after permission is granted (location provider not ready).
          // Retry once after a short delay fixes it.
          try {
            position = await Geolocation.getCurrentPosition(options);
          } catch {
            await wait(ANDROID_PERMISSION_STATUS_UPDATE_TIMEOUT);
            if (this.activeRefreshPromiseCanceled || this.status !== 'granted') {
              return this.state;
            }
            position = await Geolocation.getCurrentPosition(options);
          }
        } else {
          position = await Geolocation.getCurrentPosition(options);
        }

        const newCoords = positionToCoordinates(position);
        if (!this.coordinates || isSignificantChange(this.coordinates, newCoords)) {
          this.coordinates = newCoords;
        }
      } catch (error) {
        const isPermissionDenied =
          error && typeof error === 'object' && 'code' in error && error?.code === PERMISSION_DENIED_ERROR_CODE;
        // Allow the UI to react to state changes
        await wait(200);
        // If an exception occurs, do not clear the permission or coordinates
        // if permission has already been granted, since it may just be a temporary GPS issue
        if (isPermissionDenied || this.status !== 'granted') {
          this.coordinates = null;
          this.status = 'denied';
        }
      }

      this.checkWhenAppStateChanged();
      return this.state;
    })().finally(() => {
      this.activeRefreshPromise = null;
      this.isLoading = false;
      if (this.status === 'denied') {
        this.coordinates = null;
      }
      if (!this.activeRefreshPromiseCanceled) {
        this.notify();
      }
    });

    return this.activeRefreshPromise;
  };

  public reset = async () => {
    clearTimeout(this.pollingTimeoutId);

    await this.activeRefreshPromise?.catch(() => {});
    this.activeRefreshPromiseCanceled = true;
    this.activeRefreshPromise = null;
    this.isPolling = false;
    this.isLoading = false;
    this.coordinates = null;
    this.status = null;

    if (this.appStateListener) {
      await this.appStateListener.remove();
      this.appStateListener = undefined;
    }
  };
}

export const geolocationStore = new GeolocationStore();
